import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { TrendingUp, Users, AlertTriangle, BarChart3, Shield, Building2, ChevronDown, ChevronRight, Layers, Bot, User } from 'lucide-react';
import { useSimulaData } from '../hooks/useSimulaData';
import { useAuth } from '../context/AuthContext';
import { useTenantContext } from '../context/TenantContext';
import { useState, useMemo } from 'react';
import { isConcludedAuditStatus } from '../utils/auditStatus';

export function SectorDashboard() {
  const { user } = useAuth();
  const { tenants } = useTenantContext();
  const {
    operators: operatorsEntity,
    sectors: sectorsEntity,
    supervisors: supervisorsEntity,
    audits: auditsEntity,
    operatorStats,
  } = useSimulaData();

  const allOperators = operatorsEntity.data;
  const allSectors = sectorsEntity.data;
  const supervisors = supervisorsEntity.data;
  const audits = auditsEntity.data;

  // Supervisor filter
  const isSupervisor = user?.role === 'supervisor';
  const supervisorRecord = isSupervisor
    ? supervisors.find(s => s.id === user?.id) || supervisors.find(s => s.userId === user?.id) || supervisors[0]
    : null;
  const supervisorSectorIds = isSupervisor && supervisorRecord
    ? new Set([supervisorRecord.sectorId])
    : null;

  const sectors = supervisorSectorIds
    ? allSectors.filter(s => supervisorSectorIds.has(s.id))
    : allSectors;
  const operators = supervisorSectorIds
    ? allOperators.filter(op => supervisorSectorIds.has(op.sectorId))
    : allOperators;

  const [expandedTenants, setExpandedTenants] = useState<Set<string>>(new Set(tenants.map(t => t.id)));
  const [selectedTenantFilter, setSelectedTenantFilter] = useState<string>('');

  const toggleTenant = (tenantId: string) => {
    setExpandedTenants(prev => {
      const next = new Set(prev);
      if (next.has(tenantId)) next.delete(tenantId);
      else next.add(tenantId);
      return next;
    });
  };

  // Build tenant → sectors mapping
  const tenantSectorMap = useMemo(() => {
    const map = new Map<string, typeof sectors>();
    sectors.forEach(sector => {
      const tid = sector.tenantId || 'unknown';
      if (!map.has(tid)) map.set(tid, []);
      map.get(tid)!.push(sector);
    });
    return map;
  }, [sectors]);

  // Get tenant info by id
  const getTenantInfo = (tenantId: string) => {
    return tenants.find(t => t.id === tenantId);
  };

  // Sector stats computation
  const sectorStats = useMemo(() => {
    return sectors.map((sector) => {
      const sectorOperators = operators.filter((op) => op.sectorId === sector.id);
      const sectorOperatorIds = sectorOperators.map((op) => op.id);
      const stats = sectorOperatorIds.map((id) => operatorStats[id as keyof typeof operatorStats]).filter(Boolean);

      const avgTraining = stats.length > 0 ? stats.reduce((sum, s) => sum + s.avgTrainingScore, 0) / stats.length : 0;
      const avgAudit = stats.length > 0 ? stats.reduce((sum, s) => sum + s.avgAuditScore, 0) / stats.length : 0;
      const zeroPercentage = stats.length > 0 ? stats.reduce((sum, s) => sum + s.zeroScorePercentage, 0) / stats.length : 0;

      const sectorSupervisors = supervisors.filter(s => s.sectorId === sector.id);
      const sectorAudits = audits.filter(a => a.sectorId === sector.id && isConcludedAuditStatus(a.status) && a.evaluation);
      const aiAuditsCount = sectorAudits.filter(a => a.monitorType === 'ai').length;
      const humanAuditsCount = sectorAudits.filter(a => a.monitorType === 'human').length;

      return {
        sector,
        operatorCount: sectorOperators.length,
        avgTraining,
        avgAudit,
        avgGeneral: stats.length > 0 ? (avgTraining + avgAudit) / 2 : 0,
        zeroPercentage,
        supervisors: sectorSupervisors,
        totalAudits: sectorAudits.length,
        aiAuditsCount,
        humanAuditsCount,
      };
    });
  }, [sectors, operators, supervisors, audits, operatorStats]);

  // Tenant-level aggregated stats
  const tenantStats = useMemo(() => {
    const result: Record<string, {
      sectorCount: number;
      operatorCount: number;
      avgGeneral: number;
      totalAudits: number;
      zeroPercentage: number;
    }> = {};

    tenantSectorMap.forEach((tenantSectors, tenantId) => {
      const tStats = sectorStats.filter(ss => tenantSectors.some(s => s.id === ss.sector.id));
      const totalOps = tStats.reduce((sum, s) => sum + s.operatorCount, 0);
      const avgGen = tStats.length > 0 ? tStats.reduce((sum, s) => sum + s.avgGeneral, 0) / tStats.length : 0;
      const totalAud = tStats.reduce((sum, s) => sum + s.totalAudits, 0);
      const avgZero = tStats.length > 0 ? tStats.reduce((sum, s) => sum + s.zeroPercentage, 0) / tStats.length : 0;

      result[tenantId] = {
        sectorCount: tenantSectors.length,
        operatorCount: totalOps,
        avgGeneral: avgGen,
        totalAudits: totalAud,
        zeroPercentage: avgZero,
      };
    });

    return result;
  }, [tenantSectorMap, sectorStats]);

  // Global stats
  const globalAvg = sectorStats.length > 0
    ? sectorStats.reduce((sum, s) => sum + s.avgGeneral, 0) / sectorStats.length : 0;
  const totalOperators = operators.length;
  const globalZeroPercentage = sectorStats.length > 0
    ? sectorStats.reduce((sum, s) => sum + s.zeroPercentage, 0) / sectorStats.length : 0;

  // Filtered tenants
  const visibleTenantIds = useMemo(() => {
    const ids = Array.from(tenantSectorMap.keys());
    if (selectedTenantFilter) return ids.filter(id => id === selectedTenantFilter);
    return ids;
  }, [tenantSectorMap, selectedTenantFilter]);

  // Chart data: sectors comparison
  const chartData = useMemo(() => {
    return sectorStats
      .filter(ss => !selectedTenantFilter || ss.sector.tenantId === selectedTenantFilter)
      .map(ss => ({
        name: ss.sector.name,
        treino: Math.round(ss.avgTraining),
        auditoria: Math.round(ss.avgAudit),
        geral: Math.round(ss.avgGeneral),
      }));
  }, [sectorStats, selectedTenantFilter]);

  // Score distribution
  const visibleOperatorIds = new Set(operators.map(op => op.id));
  const allScores: number[] = [];
  Object.entries(operatorStats).forEach(([id, stat]) => {
    if (!visibleOperatorIds.has(id)) return;
    if (stat.avgTrainingScore > 0) allScores.push(stat.avgTrainingScore);
    if (stat.avgAuditScore > 0) allScores.push(stat.avgAuditScore);
  });

  const distribution = [
    { name: '0-49 (Critico)', value: allScores.filter(s => s < 50).length, color: 'var(--error)' },
    { name: '50-69 (Atencao)', value: allScores.filter(s => s >= 50 && s < 70).length, color: 'var(--warning)' },
    { name: '70-84 (Bom)', value: allScores.filter(s => s >= 70 && s < 85).length, color: 'var(--secondary)' },
    { name: '85-100 (Excelente)', value: allScores.filter(s => s >= 85).length, color: 'var(--primary)' },
  ];

  const getScoreColor = (score: number) => {
    if (score >= 85) return 'var(--primary)';
    if (score >= 70) return 'var(--secondary)';
    if (score >= 50) return 'var(--warning)';
    return 'var(--error)';
  };

  const getPlanBadge = (plan: string) => {
    const config: Record<string, { label: string; bg: string; color: string }> = {
      starter: { label: 'Starter', bg: 'color-mix(in srgb, var(--chart-4) 12%, transparent)', color: 'var(--chart-4)' },
      professional: { label: 'Professional', bg: 'color-mix(in srgb, var(--primary) 12%, transparent)', color: 'var(--primary)' },
      enterprise: { label: 'Enterprise', bg: 'color-mix(in srgb, var(--secondary) 12%, transparent)', color: 'var(--secondary)' },
    };
    return config[plan] || config.starter;
  };

  const getStatusDot = (status: string) => {
    const colors: Record<string, string> = {
      active: 'var(--success)',
      inactive: 'var(--muted-foreground)',
      trial: 'var(--warning)',
      suspended: 'var(--error)',
    };
    return colors[status] || 'var(--muted-foreground)';
  };

  return (
    <div style={{ padding: '32px', fontFamily: 'var(--font-family-inter)' }}>
      {/* Header */}
      <div className="flex items-start justify-between mb-8">
        <div>
          <h1 style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-2xl)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)' }}>
            Dashboard por Setor
          </h1>
          <p style={{ color: 'var(--muted-foreground)', marginTop: '8px', fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-base)' }}>
            {isSupervisor
              ? `Visao dos setores vinculados a voce${supervisorRecord ? ` (${sectors.map(s => s.name).join(', ')})` : ''}`
              : 'Visao consolidada de setores por empresa'}
          </p>
        </div>

        {/* Tenant filter */}
        {!isSupervisor && tenantSectorMap.size > 1 && (
          <div>
            <select
              value={selectedTenantFilter}
              onChange={(e) => setSelectedTenantFilter(e.target.value)}
              style={{
                backgroundColor: 'var(--input-background)',
                borderColor: 'var(--border)',
                borderRadius: 'var(--radius-input)',
                color: 'var(--foreground)',
                fontFamily: 'var(--font-family-inter)',
                fontSize: 'var(--text-sm)',
                padding: '8px 16px',
                border: '1px solid var(--border)',
                cursor: 'pointer',
              }}
            >
              <option value="">Todas as Empresas</option>
              {Array.from(tenantSectorMap.keys()).map(tid => {
                const t = getTenantInfo(tid);
                return (
                  <option key={tid} value={tid}>
                    {t?.name || tid}
                  </option>
                );
              })}
            </select>
          </div>
        )}
      </div>

      {/* Global Stats */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
        {[
          {
            label: 'Empresas',
            value: visibleTenantIds.length,
            icon: <Building2 size={20} style={{ color: 'var(--primary)' }} />,
            color: 'var(--foreground)',
          },
          {
            label: 'Total de Setores',
            value: sectors.filter(s => !selectedTenantFilter || s.tenantId === selectedTenantFilter).length,
            icon: <Layers size={20} style={{ color: 'var(--secondary)' }} />,
            color: 'var(--foreground)',
          },
          {
            label: 'Total de Operadores',
            value: totalOperators,
            icon: <Users size={20} style={{ color: 'var(--chart-4)' }} />,
            color: 'var(--foreground)',
          },
          {
            label: 'Media Geral',
            value: globalAvg.toFixed(0),
            icon: <TrendingUp size={20} style={{ color: getScoreColor(globalAvg) }} />,
            color: getScoreColor(globalAvg),
          },
          {
            label: '% Zeradas Global',
            value: `${globalZeroPercentage.toFixed(0)}%`,
            icon: <AlertTriangle size={20} style={{ color: globalZeroPercentage > 0 ? 'var(--error)' : 'var(--success)' }} />,
            color: globalZeroPercentage > 0 ? 'var(--error)' : 'var(--success)',
          },
        ].map((stat) => (
          <div
            key={stat.label}
            style={{
              backgroundColor: 'var(--card)',
              borderRadius: 'var(--radius-card)',
              border: '1px solid var(--border)',
              padding: '20px',
              boxShadow: 'var(--elevation-sm)',
            }}
          >
            <div className="flex items-center justify-between" style={{ marginBottom: '12px' }}>
              <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)' }}>
                {stat.label}
              </span>
              {stat.icon}
            </div>
            <span style={{
              fontSize: 'var(--text-2xl)',
              fontWeight: 'var(--font-weight-semibold)',
              fontFamily: 'var(--font-family-inter)',
              color: stat.color,
            }}>
              {stat.value}
            </span>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
        {/* Bar Chart: Sector Comparison */}
        <div
          style={{
            backgroundColor: 'var(--card)',
            borderRadius: 'var(--radius-card)',
            border: '1px solid var(--border)',
            padding: '24px',
            boxShadow: 'var(--elevation-sm)',
          }}
        >
          <h3 style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)', marginBottom: '20px' }}>
            Comparativo por Setor
          </h3>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={chartData} barGap={4}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis
                dataKey="name"
                tick={{ fill: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)', fontSize: 12 }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={{ stroke: 'var(--border)' }}
              />
              <YAxis
                domain={[0, 100]}
                tick={{ fill: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)', fontSize: 12 }}
                axisLine={{ stroke: 'var(--border)' }}
                tickLine={{ stroke: 'var(--border)' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'var(--popover)',
                  border: '1px solid var(--border)',
                  borderRadius: 'var(--radius)',
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-sm)',
                }}
                labelStyle={{ color: 'var(--popover-foreground)', fontWeight: 'var(--font-weight-semibold)' }}
                itemStyle={{ color: 'var(--popover-foreground)' }}
              />
              <Bar dataKey="treino" name="Treino" fill="var(--primary)" radius={[4, 4, 0, 0]} />
              <Bar dataKey="auditoria" name="Auditoria" fill="var(--secondary)" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Pie Chart: Score Distribution */}
        <div
          style={{
            backgroundColor: 'var(--card)',
            borderRadius: 'var(--radius-card)',
            border: '1px solid var(--border)',
            padding: '24px',
            boxShadow: 'var(--elevation-sm)',
          }}
        >
          <h3 style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)', marginBottom: '20px' }}>
            Distribuicao por Faixa de Nota
          </h3>
          <div className="flex items-center gap-6">
            <div style={{ flex: '0 0 200px' }}>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={distribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    paddingAngle={3}
                    dataKey="value"
                  >
                    {distribution.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--popover)',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius)',
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-sm)',
                    }}
                  />
                </PieChart>
              </ResponsiveContainer>
            </div>
            <div className="flex-1 space-y-3">
              {distribution.map((item) => (
                <div key={item.name} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div style={{
                      width: '12px', height: '12px',
                      backgroundColor: item.color,
                      borderRadius: '3px',
                    }} />
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)', fontFamily: 'var(--font-family-inter)' }}>
                      {item.name}
                    </span>
                  </div>
                  <span style={{
                    fontSize: 'var(--text-sm)',
                    fontWeight: 'var(--font-weight-semibold)',
                    fontFamily: 'var(--font-family-inter)',
                    color: 'var(--foreground)',
                  }}>
                    {item.value}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Tenants with Sectors - Grouped View */}
      <div style={{ marginBottom: '32px' }}>
        <h2 style={{
          fontFamily: 'var(--font-family-inter)',
          fontSize: 'var(--text-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          color: 'var(--foreground)',
          marginBottom: '16px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}>
          <Building2 size={22} style={{ color: 'var(--primary)' }} />
          Setores por Empresa
        </h2>

        <div className="space-y-4">
          {visibleTenantIds.map(tenantId => {
            const tenant = getTenantInfo(tenantId);
            const tenantSectors = tenantSectorMap.get(tenantId) || [];
            const isExpanded = expandedTenants.has(tenantId);
            const tStats = tenantStats[tenantId];
            const planBadge = tenant ? getPlanBadge(tenant.plan) : getPlanBadge('starter');

            return (
              <div
                key={tenantId}
                style={{
                  backgroundColor: 'var(--card)',
                  borderRadius: 'var(--radius-card)',
                  border: '1px solid var(--border)',
                  overflow: 'hidden',
                  boxShadow: 'var(--elevation-sm)',
                }}
              >
                {/* Tenant Header */}
                <button
                  onClick={() => toggleTenant(tenantId)}
                  className="w-full text-left"
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    padding: '20px 24px',
                    cursor: 'pointer',
                    backgroundColor: isExpanded ? 'color-mix(in srgb, var(--primary) 3%, var(--card))' : 'var(--card)',
                    borderBottom: isExpanded ? '1px solid var(--border)' : 'none',
                    transition: 'background-color 0.15s ease',
                    border: 'none',
                    width: '100%',
                    fontFamily: 'var(--font-family-inter)',
                  }}
                >
                  <div className="flex items-center gap-4">
                    <div style={{
                      width: '44px', height: '44px',
                      backgroundColor: 'color-mix(in srgb, var(--primary) 10%, transparent)',
                      borderRadius: 'var(--radius)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Building2 size={22} style={{ color: 'var(--primary)' }} />
                    </div>
                    <div>
                      <div className="flex items-center gap-3">
                        <span style={{
                          fontSize: 'var(--text-md)',
                          fontWeight: 'var(--font-weight-semibold)',
                          color: 'var(--foreground)',
                          fontFamily: 'var(--font-family-inter)',
                        }}>
                          {tenant?.name || tenantId}
                        </span>
                        {/* Status dot */}
                        {tenant && (
                          <div className="flex items-center gap-1.5">
                            <div style={{
                              width: '8px', height: '8px',
                              borderRadius: '50%',
                              backgroundColor: getStatusDot(tenant.status),
                            }} />
                            <span style={{
                              fontSize: 'var(--text-xs)',
                              color: 'var(--muted-foreground)',
                              fontFamily: 'var(--font-family-inter)',
                              textTransform: 'capitalize',
                            }}>
                              {tenant.status === 'active' ? 'Ativo' : tenant.status === 'trial' ? 'Trial' : tenant.status === 'suspended' ? 'Suspenso' : 'Inativo'}
                            </span>
                          </div>
                        )}
                        {/* Plan badge */}
                        {tenant && (
                          <span style={{
                            fontSize: 'var(--text-xs)',
                            fontWeight: 'var(--font-weight-medium)',
                            fontFamily: 'var(--font-family-inter)',
                            padding: '2px 10px',
                            borderRadius: '999px',
                            backgroundColor: planBadge.bg,
                            color: planBadge.color,
                          }}>
                            {planBadge.label}
                          </span>
                        )}
                      </div>
                      {tenant && (
                        <span style={{
                          fontSize: 'var(--text-sm)',
                          color: 'var(--muted-foreground)',
                          fontFamily: 'var(--font-family-inter)',
                          marginTop: '2px',
                          display: 'block',
                        }}>
                          {tenant.domain}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-6">
                    {/* Inline stats */}
                    {tStats && (
                      <div className="hidden md:flex items-center gap-5">
                        <div className="text-center">
                          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)', display: 'block' }}>Setores</span>
                          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)', fontFamily: 'var(--font-family-inter)' }}>{tStats.sectorCount}</span>
                        </div>
                        <div style={{ width: '1px', height: '28px', backgroundColor: 'var(--border)' }} />
                        <div className="text-center">
                          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)', display: 'block' }}>Operadores</span>
                          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)', fontFamily: 'var(--font-family-inter)' }}>{tStats.operatorCount}</span>
                        </div>
                        <div style={{ width: '1px', height: '28px', backgroundColor: 'var(--border)' }} />
                        <div className="text-center">
                          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)', display: 'block' }}>Media</span>
                          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', fontFamily: 'var(--font-family-inter)', color: getScoreColor(tStats.avgGeneral) }}>{tStats.avgGeneral.toFixed(0)}</span>
                        </div>
                        <div style={{ width: '1px', height: '28px', backgroundColor: 'var(--border)' }} />
                        <div className="text-center">
                          <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)', display: 'block' }}>Auditorias</span>
                          <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)', fontFamily: 'var(--font-family-inter)' }}>{tStats.totalAudits}</span>
                        </div>
                      </div>
                    )}
                    {isExpanded
                      ? <ChevronDown size={20} style={{ color: 'var(--muted-foreground)' }} />
                      : <ChevronRight size={20} style={{ color: 'var(--muted-foreground)' }} />
                    }
                  </div>
                </button>

                {/* Expanded Sectors */}
                {isExpanded && (
                  <div style={{ padding: '16px 24px 24px' }}>
                    {/* Sector Cards Grid */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {tenantSectors.map(sector => {
                        const ss = sectorStats.find(s => s.sector.id === sector.id);
                        if (!ss) return null;

                        const scoreBarWidth = ss.avgGeneral > 0 ? `${Math.min(ss.avgGeneral, 100)}%` : '0%';

                        return (
                          <div
                            key={sector.id}
                            style={{
                              backgroundColor: 'var(--background)',
                              borderRadius: 'var(--radius)',
                              border: '1px solid var(--border)',
                              padding: '20px',
                              transition: 'box-shadow 0.15s ease',
                            }}
                            onMouseEnter={(e) => { e.currentTarget.style.boxShadow = 'var(--elevation-sm)'; }}
                            onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                          >
                            {/* Sector Header */}
                            <div className="flex items-center justify-between" style={{ marginBottom: '16px' }}>
                              <div className="flex items-center gap-3">
                                <div style={{
                                  width: '36px', height: '36px',
                                  backgroundColor: 'color-mix(in srgb, var(--secondary) 12%, transparent)',
                                  borderRadius: 'var(--radius-input)',
                                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                  <Layers size={18} style={{ color: 'var(--secondary)' }} />
                                </div>
                                <div>
                                  <span style={{
                                    fontSize: 'var(--text-base)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    color: 'var(--foreground)',
                                    fontFamily: 'var(--font-family-inter)',
                                    display: 'block',
                                  }}>
                                    {sector.name}
                                  </span>
                                  <div className="flex items-center gap-1" style={{ marginTop: '2px' }}>
                                    <Shield size={12} style={{ color: 'var(--primary)' }} />
                                    <span style={{
                                      fontSize: 'var(--text-xs)',
                                      color: 'var(--muted-foreground)',
                                      fontFamily: 'var(--font-family-inter)',
                                    }}>
                                      {ss.supervisors.map(s => s.name).join(', ') || 'Sem supervisor'}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              {/* Operator count badge */}
                              <div style={{
                                display: 'flex', alignItems: 'center', gap: '4px',
                                padding: '4px 10px',
                                backgroundColor: 'color-mix(in srgb, var(--chart-4) 10%, transparent)',
                                borderRadius: '999px',
                              }}>
                                <Users size={14} style={{ color: 'var(--chart-4)' }} />
                                <span style={{
                                  fontSize: 'var(--text-xs)',
                                  fontWeight: 'var(--font-weight-semibold)',
                                  color: 'var(--chart-4)',
                                  fontFamily: 'var(--font-family-inter)',
                                }}>
                                  {ss.operatorCount}
                                </span>
                              </div>
                            </div>

                            {/* Score Bar */}
                            <div style={{ marginBottom: '14px' }}>
                              <div className="flex items-center justify-between" style={{ marginBottom: '6px' }}>
                                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)' }}>
                                  Media Geral
                                </span>
                                <span style={{
                                  fontSize: 'var(--text-sm)',
                                  fontWeight: 'var(--font-weight-semibold)',
                                  color: getScoreColor(ss.avgGeneral),
                                  fontFamily: 'var(--font-family-inter)',
                                }}>
                                  {ss.avgGeneral > 0 ? ss.avgGeneral.toFixed(0) : '--'}
                                </span>
                              </div>
                              <div style={{
                                width: '100%', height: '6px',
                                backgroundColor: 'var(--muted)',
                                borderRadius: '3px',
                                overflow: 'hidden',
                              }}>
                                <div style={{
                                  width: scoreBarWidth,
                                  height: '100%',
                                  backgroundColor: getScoreColor(ss.avgGeneral),
                                  borderRadius: '3px',
                                  transition: 'width 0.3s ease',
                                }} />
                              </div>
                            </div>

                            {/* Stats Grid */}
                            <div className="grid grid-cols-2 gap-3">
                              <div style={{
                                padding: '10px',
                                backgroundColor: 'var(--card)',
                                borderRadius: 'var(--radius-input)',
                                border: '1px solid var(--border)',
                              }}>
                                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)', display: 'block' }}>
                                  Media Treino
                                </span>
                                <span style={{
                                  fontSize: 'var(--text-sm)',
                                  fontWeight: 'var(--font-weight-semibold)',
                                  fontFamily: 'var(--font-family-inter)',
                                  color: ss.avgTraining > 0 ? getScoreColor(ss.avgTraining) : 'var(--muted-foreground)',
                                }}>
                                  {ss.avgTraining > 0 ? ss.avgTraining.toFixed(0) : '--'}
                                </span>
                              </div>
                              <div style={{
                                padding: '10px',
                                backgroundColor: 'var(--card)',
                                borderRadius: 'var(--radius-input)',
                                border: '1px solid var(--border)',
                              }}>
                                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)', display: 'block' }}>
                                  Media Auditoria
                                </span>
                                <span style={{
                                  fontSize: 'var(--text-sm)',
                                  fontWeight: 'var(--font-weight-semibold)',
                                  fontFamily: 'var(--font-family-inter)',
                                  color: ss.avgAudit > 0 ? getScoreColor(ss.avgAudit) : 'var(--muted-foreground)',
                                }}>
                                  {ss.avgAudit > 0 ? ss.avgAudit.toFixed(0) : '--'}
                                </span>
                              </div>
                              <div style={{
                                padding: '10px',
                                backgroundColor: 'var(--card)',
                                borderRadius: 'var(--radius-input)',
                                border: '1px solid var(--border)',
                              }}>
                                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)', display: 'block' }}>
                                  % Zeradas
                                </span>
                                <span style={{
                                  fontSize: 'var(--text-sm)',
                                  fontWeight: 'var(--font-weight-semibold)',
                                  fontFamily: 'var(--font-family-inter)',
                                  color: ss.zeroPercentage > 0 ? 'var(--error)' : 'var(--success)',
                                }}>
                                  {ss.zeroPercentage.toFixed(0)}%
                                </span>
                              </div>
                              <div style={{
                                padding: '10px',
                                backgroundColor: 'var(--card)',
                                borderRadius: 'var(--radius-input)',
                                border: '1px solid var(--border)',
                              }}>
                                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)', display: 'block' }}>
                                  Auditorias
                                </span>
                                <div className="flex items-center gap-2">
                                  <span style={{
                                    fontSize: 'var(--text-sm)',
                                    fontWeight: 'var(--font-weight-semibold)',
                                    fontFamily: 'var(--font-family-inter)',
                                    color: 'var(--foreground)',
                                  }}>
                                    {ss.totalAudits}
                                  </span>
                                  {ss.totalAudits > 0 && (
                                    <span className="flex items-center gap-1" style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)' }}>
                                      <Bot size={10} />{ss.aiAuditsCount}
                                      <User size={10} style={{ marginLeft: '4px' }} />{ss.humanAuditsCount}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Comparison Table */}
      <div
        style={{
          backgroundColor: 'var(--card)',
          borderRadius: 'var(--radius-card)',
          border: '1px solid var(--border)',
          overflow: 'hidden',
          boxShadow: 'var(--elevation-sm)',
        }}
      >
        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--border)' }}>
          <h3 style={{ fontFamily: 'var(--font-family-inter)', fontSize: 'var(--text-lg)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)' }}>
            Comparativo Completo
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full" style={{ fontFamily: 'var(--font-family-inter)' }}>
            <thead>
              <tr style={{ backgroundColor: 'var(--muted)', borderBottom: '1px solid var(--border)' }}>
                {['Empresa', 'Setor', 'Supervisor', 'Operadores', 'Med. Treino', 'Med. Auditoria', 'Med. Geral', '% Zeradas', 'Auditorias'].map(header => (
                  <th
                    key={header}
                    style={{
                      padding: '12px 16px',
                      textAlign: header === 'Empresa' || header === 'Setor' || header === 'Supervisor' ? 'left' : 'center',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--muted-foreground)',
                      fontFamily: 'var(--font-family-inter)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sectorStats
                .filter(ss => !selectedTenantFilter || ss.sector.tenantId === selectedTenantFilter)
                .map((stat) => {
                  const tenant = getTenantInfo(stat.sector.tenantId);
                  return (
                    <tr
                      key={stat.sector.id}
                      style={{ borderBottom: '1px solid var(--border)' }}
                      onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'var(--muted)'; }}
                      onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                    >
                      <td style={{ padding: '14px 16px' }}>
                        <div className="flex items-center gap-2">
                          <Building2 size={14} style={{ color: 'var(--primary)', flexShrink: 0 }} />
                          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--foreground)', fontFamily: 'var(--font-family-inter)', fontWeight: 'var(--font-weight-medium)' }}>
                            {tenant?.name || stat.sector.tenantId}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-weight-semibold)', color: 'var(--foreground)', fontFamily: 'var(--font-family-inter)' }}>
                          {stat.sector.name}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div className="flex items-center gap-1">
                          <Shield size={12} style={{ color: 'var(--primary)' }} />
                          <span style={{ fontSize: 'var(--text-sm)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)' }}>
                            {stat.supervisors.map(s => s.name).join(', ') || 'N/A'}
                          </span>
                        </div>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center', fontSize: 'var(--text-sm)', fontFamily: 'var(--font-family-inter)' }}>
                        {stat.operatorCount}
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-weight-semibold)',
                          fontFamily: 'var(--font-family-inter)',
                          color: stat.avgTraining > 0 ? getScoreColor(stat.avgTraining) : 'var(--muted-foreground)',
                        }}>
                          {stat.avgTraining > 0 ? stat.avgTraining.toFixed(0) : '--'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-weight-semibold)',
                          fontFamily: 'var(--font-family-inter)',
                          color: stat.avgAudit > 0 ? getScoreColor(stat.avgAudit) : 'var(--muted-foreground)',
                        }}>
                          {stat.avgAudit > 0 ? stat.avgAudit.toFixed(0) : '--'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-weight-semibold)',
                          fontFamily: 'var(--font-family-inter)',
                          color: stat.avgGeneral > 0 ? getScoreColor(stat.avgGeneral) : 'var(--muted-foreground)',
                        }}>
                          {stat.avgGeneral > 0 ? stat.avgGeneral.toFixed(0) : '--'}
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <span style={{
                          fontSize: 'var(--text-sm)',
                          fontWeight: 'var(--font-weight-semibold)',
                          fontFamily: 'var(--font-family-inter)',
                          color: stat.zeroPercentage > 0 ? 'var(--error)' : 'var(--success)',
                        }}>
                          {stat.zeroPercentage.toFixed(0)}%
                        </span>
                      </td>
                      <td style={{ padding: '14px 16px', textAlign: 'center' }}>
                        <div className="flex items-center justify-center gap-2">
                          <span style={{ fontSize: 'var(--text-sm)', fontFamily: 'var(--font-family-inter)', color: 'var(--foreground)' }}>
                            {stat.totalAudits}
                          </span>
                          {stat.totalAudits > 0 && (
                            <span className="flex items-center gap-1" style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)', fontFamily: 'var(--font-family-inter)' }}>
                              (<Bot size={10} />{stat.aiAuditsCount} / <User size={10} />{stat.humanAuditsCount})
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
